import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState, useEffect} from 'react';

// eslint-disable-next-line import-access/jsdoc
import {FormState} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
import {MAX_NUM_QUERY_API_FIELDS} from 'analysis-tool/data-frame-creation/schemas/SchemaDatasetCreationConstants';
import {sum} from 'BenchlingUtil';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {
  maybeGetValueFromConfig,
  setValueInConfig,
} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import countSelectedFields from 'query-api/graphql-field-selector/countSelectedFields';
import useQueryApiIntrospection from 'query-api/useQueryApiIntrospection';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  DEFAULT_QUERY_FORM_STATE,
  buildInitialQueryFormStateFromQueryString,
  buildQueryStringFromFormState,
} from './LookUpQueryUtils';
import LookUpSchemaSelectorStepper from './LookUpSchemaSelectorStepper';

const LOOKUP_DATA_SUFFIX = ' lookup data';

const shouldUpdateDataFrameName = (
  currentDataFrameName: string | null | undefined,
  currentSchemaName: string | undefined
): boolean => {
  // If the dataFrameName is not set, we'll generate a name
  if (!currentDataFrameName) {
    return true;
  }

  // If the dataFrameName is set, we'll check if it matches
  // the pattern we use to generate names
  return (
    currentSchemaName !== undefined && currentDataFrameName === `${currentSchemaName}${LOOKUP_DATA_SUFFIX}`
  );
};

const maybeUpdateDataFrameName = (
  config: CustomComponentEditorProps['config'],
  newSchemaId: string | null,
  queryApiIntrospection: ReturnType<typeof useQueryApiIntrospection>
): string => {
  let dataFrameName = config.dataFrameName;

  if (
    newSchemaId &&
    newSchemaId !== config.schemaId &&
    queryApiIntrospection.status === QueryResultType.SUCCESS
  ) {
    // Get the current and new schema names
    const allSchemas = queryApiIntrospection.getAllListableSchemas();
    let newSchemaName;
    let currentSchemaName;
    for (const schema of allSchemas) {
      if (schema.id === newSchemaId) {
        newSchemaName = schema.name;
      }
      if (schema.id === config.schemaId) {
        currentSchemaName = schema.name;
      }
    }
    if (newSchemaName) {
      if (shouldUpdateDataFrameName(config.dataFrameName as string | null | undefined, currentSchemaName)) {
        dataFrameName = `${newSchemaName}${LOOKUP_DATA_SUFFIX}`;
      }
    }
  }

  return dataFrameName as string;
};

export default function LookUpFieldEditor({
  config,
  onChangeConfig,
  disabled,
  configShape,
}: CustomComponentEditorProps): JSX.Element {
  const [queryFormState, setQueryFormState] = useState<FormState>(DEFAULT_QUERY_FORM_STATE);
  const [errorMessages, setErrorMessages] = useState<Array<string> | null>(null);
  const queryApiIntrospection = useQueryApiIntrospection();
  const maybeSchemaId = maybeGetValueFromConfig(config, ['schemaId']) as string | null;
  const maybeQueryString = maybeGetValueFromConfig(config, ['query']) as string | null;

  useEffect(() => {
    if (queryApiIntrospection.status !== QueryResultType.SUCCESS) {
      return;
    }
    const result = buildInitialQueryFormStateFromQueryString(
      maybeSchemaId,
      maybeQueryString,
      queryApiIntrospection
    );
    setQueryFormState(result.formState);
    setErrorMessages(result.errorMessages ?? null);
  }, [maybeQueryString, maybeSchemaId, queryApiIntrospection]);

  const handleChangeQueryFormState = useCallback(
    (newFormState: FormState) => {
      const totalFields =
        newFormState.entityListingFieldName == null
          ? 0
          : countSelectedFields(newFormState.entityFieldSelections.selections) +
            sum(
              newFormState.relatedResultsFormStates.map((resultFormState) =>
                countSelectedFields(resultFormState.backlinkSelections.selections)
              )
            );
      if (totalFields > MAX_NUM_QUERY_API_FIELDS) {
        notifications.error(`Only ${MAX_NUM_QUERY_API_FIELDS} column selections are allowed.`, {
          duration: 4,
        });
      } else {
        setQueryFormState(newFormState);
      }
      const queryString = buildQueryStringFromFormState(newFormState);
      const newSchemaId =
        queryApiIntrospection.status !== QueryResultType.ERROR &&
        queryApiIntrospection.status !== QueryResultType.PENDING &&
        newFormState.entityListingFieldName != null
          ? queryApiIntrospection.getQueryFieldSchemaId(newFormState.entityListingFieldName)
          : null;

      // TODO: Set three fields on a config at once
      const configWithQuerySet = setValueInConfig(config, ['query'], queryString);
      const dataFrameName = maybeUpdateDataFrameName(config, newSchemaId, queryApiIntrospection);
      const configWithDataFrameNameSet = setValueInConfig(
        configWithQuerySet,
        ['dataFrameName'],
        dataFrameName
      );

      onChangeConfig(configWithDataFrameNameSet, ['schemaId'], newSchemaId);
    },
    [config, onChangeConfig, queryApiIntrospection, setQueryFormState]
  );

  return (
    <div className={css(styles.bannerWrapper)}>
      {errorMessages && errorMessages.length > 0 && (
        <>
          {errorMessages.map((message, index) => (
            <InlineBanner key={index} bannerType={InlineBannerType.WARNING} text={message} />
          ))}
        </>
      )}
      {queryApiIntrospection.status === QueryResultType.ERROR ? (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading registry schemas." />
      ) : queryApiIntrospection.status === QueryResultType.PENDING ? (
        <CenteredHorizontalThrobber />
      ) : (
        <LookUpSchemaSelectorStepper
          queryApiIntrospection={queryApiIntrospection}
          queryFormState={queryFormState}
          setQueryFormState={handleChangeQueryFormState}
          disabled={disabled}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  bannerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingBottom: '5px',
  },
});
